/**
 * Ad Unit Factory - Generates Prebid ad units with brand-specific bidder parameters
 *
 * This module provides factory functions for creating Prebid ad units
 * that are parameterized by brand configuration from brandConfigs.js
 */

import { getBrandConfig } from './brandConfigs.js';

// Standard ad sizes by placement type (article pages)
export const AD_SIZES = {
  desktop: {
    topAd: [
      [970, 250],
      [970, 90],
      [728, 90],
      [320, 50],
      [320, 100],
      [300, 250],
      [300, 100]
    ],
    content: [
      [728, 90],
      [300, 600],
      [320, 480],
      [300, 250],
      [336, 280],
      [320, 50],
      [320, 100],
      [1, 1],
      'fluid'
    ],
    sidebar: [
      [300, 600],
      [300, 250],
      [160, 600]
    ],
    skin: [[2000, 1200], [2000, 1], [1, 1]]
  },
  mobile: {
    leaderboard: [
      [320, 50],
      [300, 50],
      [320, 100],
      [300, 100]
    ],
    content: [
      [320, 50],
      [320, 100],
      [320, 480],
      [300, 250],
      [300, 100],
      [336, 280],
      [1, 1],
      'fluid'
    ]
  }
};

// Homepage-specific ad sizes (simpler than article pages)
export const HOMEPAGE_AD_SIZES = {
  desktop: {
    topAd: [
      [728, 90],
      [970, 250],
      [2000, 250]
    ],
    content: [
      [300, 250],
      [336, 280],
      [320, 480],
      [1, 1]
    ],
    skin: [[2000, 1200]],
    tabletSticky: [
      [728, 90],
      [320, 50],
      [300, 50]
    ]
  },
  mobile: {
    leaderboard: [
      [320, 50],
      [300, 50],
      [320, 100],
      [300, 100]
    ],
    content: [
      [320, 50],
      [320, 100],
      [320, 480],
      [300, 250],
      [300, 100],
      [336, 280],
      [1, 1]
    ]
  }
};

const STORIES_VIDEO_DEFAULT_PLAYER_SIZE = [300, 500];

/**
 * Creates bid configuration array for a given placement
 * @param {object} config - Brand configuration
 * @param {string} device - 'desktop' or 'mobile'
 * @param {string} placement - Placement type: 'topAd', 'content', 'sidebar', 'leaderboard'
 * @param {number} index - Ad unit index
 * @param {object} options - Additional options
 * @param {boolean} options.includeTeads - Whether to include Teads bidder
 * @returns {Array} Array of bid configurations
 */
function createBids(config, device, placement, index, options = {}) {
  const { includeTeads = false } = options;
  const bidders = config.bidders;

  // Map placement to bidder key
  const bidderKeyMap = {
    topAd: 'topAd',
    content: 'content',
    sidebar: 'sidebar',
    leaderboard: 'leaderboard'
  };
  const bidderKey = bidderKeyMap[placement] || placement;

  // Get device-specific bidder params
  const deviceBidders = device === 'mobile' ? 'mobile' : 'desktop';

  const bids = [];

  // IX
  const ixSiteId = bidders.ix[deviceBidders]?.[bidderKey];
  if (ixSiteId) {
    bids.push({
      bidder: 'ix',
      params: {
        siteId: parseInt(ixSiteId, 10)
      }
    });
  }

  // Teads (only for content placements)
  if (includeTeads && bidders.teads) {
    bids.push({
      bidder: 'teads',
      params: {
        pageId: bidders.teads.pageId,
        placementId: bidders.teads.placementId
      }
    });
  }

  // AppNexus
  const appnexusPlacementId = bidders.appnexus[deviceBidders]?.[bidderKey];
  if (appnexusPlacementId) {
    bids.push({
      bidder: 'appnexus',
      params: {
        placement_id: appnexusPlacementId
      }
    });
  }

  // Rubicon
  const rubiconZone = bidders.rubicon.zones[deviceBidders]?.[bidderKey];
  if (rubiconZone) {
    bids.push({
      bidder: 'rubicon',
      params: {
        accountId: bidders.rubicon.accountId,
        siteId: bidders.rubicon.siteId,
        zoneId: rubiconZone
      }
    });
  }

  // Yahoo SSP
  if (bidders.yahooSsp) {
    bids.push({
      bidder: 'yahoossp',
      params: {
        dcn: bidders.yahooSsp.dcn,
        pos: bidders.yahooSsp.pos
      }
    });
  }

  // PubMatic
  if (bidders.pubmatic) {
    bids.push({
      bidder: 'pubmatic',
      params: {
        publisherId: bidders.pubmatic.publisherId
      }
    });
  }

  // TTD
  if (bidders.ttd) {
    bids.push({
      bidder: 'ttd',
      params: {
        supplySourceId: bidders.ttd.supplySourceId,
        publisherId: bidders.ttd.publisherId
      }
    });
  }

  return bids;
}

function isUsableBidValue(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return false;
}

function createVideoBids(config, placement = 'preroll') {
  const bidders = config?.bidders || {};
  const bids = [];

  const appnexusPlacementId = bidders?.appnexus?.video?.[placement];
  if (isUsableBidValue(appnexusPlacementId)) {
    bids.push({
      bidder: 'appnexus',
      params: {
        placement_id: appnexusPlacementId
      }
    });
  }

  const rubiconZoneId = bidders?.rubicon?.zones?.video?.[placement]
    || bidders?.rubicon?.video?.[placement];
  if (
    isUsableBidValue(rubiconZoneId)
    && isUsableBidValue(bidders?.rubicon?.accountId)
    && isUsableBidValue(bidders?.rubicon?.siteId)
  ) {
    bids.push({
      bidder: 'rubicon',
      params: {
        accountId: bidders.rubicon.accountId,
        siteId: bidders.rubicon.siteId,
        zoneId: rubiconZoneId
      }
    });
  }

  const ixSiteId = bidders?.ix?.video?.[placement];
  if (isUsableBidValue(ixSiteId)) {
    const parsedIxSiteId = parseInt(ixSiteId, 10);
    if (Number.isFinite(parsedIxSiteId) && parsedIxSiteId > 0) {
      bids.push({
        bidder: 'ix',
        params: {
          siteId: parsedIxSiteId
        }
      });
    }
  }

  const yahooVideoPosition = bidders?.yahooSsp?.video?.[placement];
  if (
    isUsableBidValue(yahooVideoPosition)
    && isUsableBidValue(bidders?.yahooSsp?.dcn)
  ) {
    bids.push({
      bidder: 'yahoossp',
      params: {
        dcn: bidders.yahooSsp.dcn,
        pos: yahooVideoPosition
      }
    });
  }

  const pubmaticVideoAdSlot = bidders?.pubmatic?.adSlots?.video?.[placement];
  if (
    isUsableBidValue(pubmaticVideoAdSlot)
    && isUsableBidValue(bidders?.pubmatic?.publisherId)
  ) {
    bids.push({
      bidder: 'pubmatic',
      params: {
        publisherId: bidders.pubmatic.publisherId,
        adSlot: pubmaticVideoAdSlot
      }
    });
  }

  const ttdVideoPlacementId = bidders?.ttd?.video?.[placement];
  if (
    isUsableBidValue(ttdVideoPlacementId)
    && isUsableBidValue(bidders?.ttd?.supplySourceId)
    && isUsableBidValue(bidders?.ttd?.publisherId)
  ) {
    bids.push({
      bidder: 'ttd',
      params: {
        supplySourceId: bidders.ttd.supplySourceId,
        publisherId: bidders.ttd.publisherId,
        placementId: ttdVideoPlacementId
      }
    });
  }

  const teadsVideoPlacementId = bidders?.teads?.video?.[placement];
  if (
    isUsableBidValue(teadsVideoPlacementId)
    && isUsableBidValue(bidders?.teads?.pageId)
  ) {
    bids.push({
      bidder: 'teads',
      params: {
        pageId: bidders.teads.pageId,
        placementId: teadsVideoPlacementId
      }
    });
  }

  return bids;
}

/**
 * Creates a desktop top ad unit
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Optional settings
 * @param {Array} options.sizes - Custom sizes array (defaults to AD_SIZES.desktop.topAd)
 * @returns {object} Prebid ad unit
 */
export function makeTopAdUnit(config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const sizes = options.sizes || AD_SIZES.desktop.topAd;
  const code = typeof options.code === 'string' && options.code.trim()
    ? options.code.trim()
    : 'top-ad';
  const index = Number.isFinite(Number(options.index)) && Number(options.index) > 0
    ? Number(options.index)
    : 1;
  const slotPath = `${config.gamNetwork}/${config.slots.desktop.topAd}`;

  return {
    code,
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#${index}`,
        pbadslot: `${slotPath}#${index}`
      }
    },
    mediaTypes: {
      banner: {
        sizes
      }
    },
    bids: createBids(config, 'desktop', 'topAd', 1)
  };
}

/**
 * Creates a desktop skin ad unit (no bidders - just GPT)
 * @param {object|string} config - Brand configuration object or brand name
 * @returns {object} Prebid ad unit
 */
export function makeSkinAdUnit(config) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  return {
    code: 'skin-ad',
    mediaTypes: {
      banner: {
        sizes: AD_SIZES.desktop.skin
      }
    }
    // Skin ads typically don't have Prebid bidders
  };
}

/**
 * Creates a desktop content ad unit
 * @param {number} index - Ad unit index (1-based)
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Optional settings
 * @param {Array} options.sizes - Custom sizes array (defaults to AD_SIZES.desktop.content)
 * @returns {object} Prebid ad unit
 */
export function makeDtContentAdUnit(index, config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const sizes = options.sizes || AD_SIZES.desktop.content;
  const slotPath = `${config.gamNetwork}/${config.slots.desktop.content}`;

  return {
    code: `dt-content-ad-${index}`,
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#${index}`,
        pbadslot: `${slotPath}#${index}`
      }
    },
    mediaTypes: {
      banner: {
        sizes
      }
    },
    bids: createBids(config, 'desktop', 'content', index, { includeTeads: true })
  };
}

/**
 * Creates a desktop sidebar ad unit
 * @param {number} index - Ad unit index (1-based)
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Optional settings
 * @param {Array} options.sizes - Custom sizes array (defaults to AD_SIZES.desktop.sidebar)
 * @returns {object} Prebid ad unit
 */
export function makeSidebarAdUnit(index, config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const sizes = options.sizes || AD_SIZES.desktop.sidebar;
  const slotPath = `${config.gamNetwork}/${config.slots.desktop.sidebar}`;

  return {
    code: `sidebar-ad-${index}`,
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#${index}`,
        pbadslot: `${slotPath}#${index}`
      }
    },
    mediaTypes: {
      banner: {
        sizes
      }
    },
    bids: createBids(config, 'desktop', 'sidebar', index)
  };
}

export function makeStoriesVideoAdUnit(config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const {
    code = 'stories-video',
    placement = 'preroll',
    plcmt = 2,
    playerSize = STORIES_VIDEO_DEFAULT_PLAYER_SIZE
  } = options;

  const normalizedPlayerSize = Array.isArray(playerSize) && playerSize.length === 2
    ? playerSize.map((value) => Number(value))
    : STORIES_VIDEO_DEFAULT_PLAYER_SIZE;
  const width = Number.isFinite(normalizedPlayerSize[0]) && normalizedPlayerSize[0] > 0
    ? normalizedPlayerSize[0]
    : STORIES_VIDEO_DEFAULT_PLAYER_SIZE[0];
  const height = Number.isFinite(normalizedPlayerSize[1]) && normalizedPlayerSize[1] > 0
    ? normalizedPlayerSize[1]
    : STORIES_VIDEO_DEFAULT_PLAYER_SIZE[1];

  return {
    code,
    mediaTypes: {
      video: {
        context: 'instream',
        // OpenRTB 2.6 placement, matching the GAM tag: 2 = Accompanying Content
        // that starts muted when it scrolls into view (playbackmethod 6),
        // 1 = In-stream on the Shows site.
        plcmt,
        ...(plcmt === 2 ? { playbackmethod: [6] } : {}),
        playerSize: [width, height],
        mimes: ['video/mp4', 'video/webm'],
        protocols: [2, 3, 5, 6, 7],
        linearity: 1,
        maxduration: 30,
        // Between-clip ads are generic mid-rolls (-1); only the first is a pre-roll.
        startdelay: placement === 'interstitial' ? -1 : 0,
        skip: 1,
        skipafter: 5
      }
    },
    bids: createVideoBids(config, placement)
  };
}

/**
 * Creates a mobile leaderboard ad unit
 * @param {number} index - Ad unit index (1-based)
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Optional settings
 * @param {Array} options.sizes - Custom sizes array (defaults to AD_SIZES.mobile.leaderboard)
 * @returns {object} Prebid ad unit
 */
export function makeMobileLbAdUnit(index, config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const sizes = options.sizes || AD_SIZES.mobile.leaderboard;
  const slotPath = `${config.gamNetwork}/${config.slots.mobile.leaderboard}`;
  const codePrefix = config.brand === 'mtlblog' ? 'mobile-lb-mtlblog' : 'mobile-lb-narcity';

  return {
    code: `${codePrefix}-${index}`,
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#${index}`,
        pbadslot: `${slotPath}#${index}`
      }
    },
    mediaTypes: {
      banner: {
        sizes
      }
    },
    bids: createBids(config, 'mobile', 'leaderboard', index)
  };
}

/**
 * Creates a mobile content ad unit
 * @param {number} index - Ad unit index (1-based)
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Optional settings
 * @param {Array} options.sizes - Custom sizes array (defaults to AD_SIZES.mobile.content)
 * @returns {object} Prebid ad unit
 */
export function makeMobileContentAdUnit(index, config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const sizes = options.sizes || AD_SIZES.mobile.content;
  const slotPath = `${config.gamNetwork}/${config.slots.mobile.content}`;
  const codePrefix = config.brand === 'mtlblog' ? 'bb-content-mtlblog' : 'bb-content-narcity';

  return {
    code: `${codePrefix}-${index}`,
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#${index}`,
        pbadslot: `${slotPath}#${index}`
      }
    },
    mediaTypes: {
      banner: {
        sizes
      }
    },
    bids: createBids(config, 'mobile', 'content', index, { includeTeads: true })
  };
}

/**
 * Generates initial ad units for desktop article pages
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Generation options
 * @param {number} options.contentCount - Number of content ad units (default: 7)
 * @param {number} options.sidebarCount - Number of sidebar ad units (default: 4)
 * @param {Array} options.contentSizes - Custom content sizes (optional)
 * @param {Array} options.sidebarSizes - Custom sidebar sizes (optional)
 * @returns {Array} Array of Prebid ad units
 */
export function generateDesktopArticleAdUnits(config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const { contentCount = 7, sidebarCount = 4, contentSizes, sidebarSizes } = options;
  const adUnits = [];

  // Top ad and skin
  adUnits.push(makeTopAdUnit(config));
  adUnits.push(makeSkinAdUnit(config));

  // Content ads
  for (let i = 1; i <= contentCount; i++) {
    adUnits.push(makeDtContentAdUnit(i, config, { sizes: contentSizes }));
  }

  // Sidebar ads
  for (let i = 1; i <= sidebarCount; i++) {
    adUnits.push(makeSidebarAdUnit(i, config, { sizes: sidebarSizes }));
  }

  return adUnits;
}

/**
 * Generates initial ad units for mobile article pages
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Generation options
 * @param {number} options.leaderboardCount - Number of leaderboard ad units (default: 2)
 * @param {number} options.contentCount - Number of content ad units (default: 15)
 * @param {Array} options.leaderboardSizes - Custom leaderboard sizes (optional)
 * @param {Array} options.contentSizes - Custom content sizes (optional)
 * @param {object} options.contentSizesByIndex - Per-index content size overrides (optional)
 * @returns {Array} Array of Prebid ad units
 */
export function generateMobileArticleAdUnits(config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const {
    leaderboardCount = 2,
    contentCount = 15,
    leaderboardSizes,
    contentSizes,
    contentSizesByIndex = {}
  } = options;
  const adUnits = [];

  // Mobile leaderboard ads
  for (let i = 1; i <= leaderboardCount; i++) {
    adUnits.push(makeMobileLbAdUnit(i, config, { sizes: leaderboardSizes }));
  }

  // Mobile content ads
  for (let i = 1; i <= contentCount; i++) {
    const indexedSizes = Array.isArray(contentSizesByIndex[i])
      ? contentSizesByIndex[i]
      : contentSizes;
    adUnits.push(makeMobileContentAdUnit(i, config, { sizes: indexedSizes }));
  }

  return adUnits;
}

/**
 * Helper to ensure a Prebid ad unit exists for a given code
 * Creates the unit dynamically if missing
 * @param {object} pbjs - Prebid.js instance
 * @param {string} code - Ad unit code
 * @param {object|string} config - Brand configuration
 * @param {string} device - 'desktop' or 'mobile'
 */
export function ensurePrebidAdUnitForCode(pbjs, code, config, _device = 'desktop') {
  if (!pbjs || !code) return;

  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const adUnits = typeof pbjs.getAdUnits === 'function' ? pbjs.getAdUnits() : pbjs.adUnits;
  if (Array.isArray(adUnits) && adUnits.some((unit) => unit && unit.code === code)) {
    return; // Already exists
  }

  // Parse code to determine ad unit type and create appropriate unit
  let newUnit = null;

  // Desktop content: dt-content-ad-{index}
  const dtContentMatch = /^dt-content-ad-(\d+)$/.exec(code);
  if (dtContentMatch) {
    const index = Number(dtContentMatch[1]);
    if (Number.isFinite(index) && index > 0) {
      newUnit = makeDtContentAdUnit(index, config);
    }
  }

  // Sidebar: sidebar-ad-{index}
  const sidebarMatch = /^sidebar-ad-(\d+)$/.exec(code);
  if (sidebarMatch) {
    const index = Number(sidebarMatch[1]);
    if (Number.isFinite(index) && index > 0) {
      newUnit = makeSidebarAdUnit(index, config);
    }
  }

  // Mobile leaderboard: mobile-lb-{brand}-{index}
  const mobileLbMatch = /^mobile-lb-(?:narcity|mtlblog)-(\d+)$/.exec(code);
  if (mobileLbMatch) {
    const index = Number(mobileLbMatch[1]);
    if (Number.isFinite(index) && index > 0) {
      newUnit = makeMobileLbAdUnit(index, config);
    }
  }

  // Mobile content: bb-content-{brand}-{index}
  const mobileContentMatch = /^bb-content-(?:narcity|mtlblog)-(\d+)$/.exec(code);
  if (mobileContentMatch) {
    const index = Number(mobileContentMatch[1]);
    if (Number.isFinite(index) && index > 0) {
      newUnit = makeMobileContentAdUnit(index, config);
    }
  }

  if (newUnit) {
    pbjs.addAdUnits([newUnit]);
  }
}

// ============================================================
// Homepage-specific ad unit factories
// ============================================================

/**
 * Creates a homepage top ad unit (different sizes than article)
 * @param {object|string} config - Brand configuration object or brand name
 * @returns {object} Prebid ad unit
 */
export function makeHomepageTopAdUnit(config) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const slotPath = `${config.gamNetwork}/${config.slots.desktop.topAd}`;

  return {
    code: 'top-ad',
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#1`,
        pbadslot: `${slotPath}#1`
      }
    },
    mediaTypes: {
      banner: {
        sizes: HOMEPAGE_AD_SIZES.desktop.topAd
      }
    },
    bids: createBids(config, 'desktop', 'topAd', 1)
  };
}

/**
 * Creates a homepage skin ad unit (no bidders - just GPT)
 * @param {object|string} config - Brand configuration object or brand name
 * @returns {object} Prebid ad unit
 */
export function makeHomepageSkinAdUnit(config) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  return {
    code: 'skin-ad',
    mediaTypes: {
      banner: {
        sizes: HOMEPAGE_AD_SIZES.desktop.skin
      }
    }
    // Skin ads typically don't have Prebid bidders
  };
}

/**
 * Creates a homepage desktop content ad unit
 * @param {number} index - Ad unit index (1-based)
 * @param {object|string} config - Brand configuration object or brand name
 * @returns {object} Prebid ad unit
 */
export function makeHomepageDtContentAdUnit(index, config) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const slotPath = `${config.gamNetwork}/${config.slots.desktop.content}`;

  return {
    code: `dt-content-ad-${index}`,
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#${index}`,
        pbadslot: `${slotPath}#${index}`
      }
    },
    mediaTypes: {
      banner: {
        sizes: HOMEPAGE_AD_SIZES.desktop.content
      }
    },
    bids: createBids(config, 'desktop', 'content', index, { includeTeads: true })
  };
}

/**
 * Generates initial ad units for homepage desktop pages
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Generation options
 * @param {number} options.contentCount - Number of content ad units (default: 12)
 * @returns {Array} Array of Prebid ad units
 */
export function generateHomepageDesktopAdUnits(config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const { contentCount = 12 } = options;
  const adUnits = [];

  // Top ad and skin
  adUnits.push(makeHomepageTopAdUnit(config));
  adUnits.push(makeHomepageSkinAdUnit(config));

  // Content ads
  for (let i = 1; i <= contentCount; i++) {
    adUnits.push(makeHomepageDtContentAdUnit(i, config));
  }

  return adUnits;
}

/**
 * Creates a homepage mobile leaderboard ad unit
 * @param {number} index - Ad unit index (1-based)
 * @param {object|string} config - Brand configuration object or brand name
 * @returns {object} Prebid ad unit
 */
export function makeHomepageMobileLbAdUnit(index, config) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const slotPath = `${config.gamNetwork}/${config.slots.mobile.leaderboard}`;
  const codePrefix = config.brand === 'mtlblog' ? 'mobile-lb-mtlblog' : 'mobile-lb-narcity';

  return {
    code: `${codePrefix}-${index}`,
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#${index}`,
        pbadslot: `${slotPath}#${index}`
      }
    },
    mediaTypes: {
      banner: {
        sizes: HOMEPAGE_AD_SIZES.mobile.leaderboard
      }
    },
    bids: createBids(config, 'mobile', 'leaderboard', index)
  };
}

/**
 * Creates a homepage mobile content ad unit
 * @param {number} index - Ad unit index (1-based)
 * @param {object|string} config - Brand configuration object or brand name
 * @returns {object} Prebid ad unit
 */
export function makeHomepageMobileContentAdUnit(index, config) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const slotPath = `${config.gamNetwork}/${config.slots.mobile.content}`;
  const codePrefix = config.brand === 'mtlblog' ? 'bb-content-mtlblog' : 'bb-content-narcity';

  return {
    code: `${codePrefix}-${index}`,
    ortb2Imp: {
      ext: {
        gpid: `${slotPath}#${index}`,
        pbadslot: `${slotPath}#${index}`
      }
    },
    mediaTypes: {
      banner: {
        sizes: HOMEPAGE_AD_SIZES.mobile.content
      }
    },
    bids: createBids(config, 'mobile', 'content', index, { includeTeads: true })
  };
}

/**
 * Generates initial ad units for homepage mobile pages
 * @param {object|string} config - Brand configuration object or brand name
 * @param {object} options - Generation options
 * @param {number} options.contentCount - Number of content ad units (default: 12)
 * @returns {Array} Array of Prebid ad units
 */
export function generateHomepageMobileAdUnits(config, options = {}) {
  if (typeof config === 'string') {
    config = getBrandConfig(config);
  }

  const { contentCount = 12 } = options;
  const adUnits = [];

  // Mobile leaderboard (1 at top)
  adUnits.push(makeHomepageMobileLbAdUnit(1, config));

  // Mobile content ads
  for (let i = 1; i <= contentCount; i++) {
    adUnits.push(makeHomepageMobileContentAdUnit(i, config));
  }

  return adUnits;
}
